import React from "react";

type Cache<T> = T & { versionID: string };

export function useCache<T extends object>(input: {
  versionID: string;
  key: string;
  defaultState: T;
}) {
  const inputRef = React.useRef(input);
  const { versionID, key, defaultState } = inputRef.current;

  function get(): T {
    const cache = getFromStorage<Cache<T>>(key);

    if (!cache) {
      set(defaultState);
      return defaultState;
    }

    if (cache.versionID !== versionID) {
      removeFromStorage(key);
      set(defaultState);
      return defaultState;
    }

    return cache;
  }

  function set(state: T): void {
    setToStorage(key, {
      ...state,
      versionID,
    });
  }

  return {
    get,
    set,
  };
}

function setToStorage<T extends object>(name: string, state: T) {
  localStorage.setItem(name, JSON.stringify(state));
}

function removeFromStorage(name: string) {
  localStorage.removeItem(name);
}

function getFromStorage<T extends object>(name: string): T | undefined {
  const it = localStorage.getItem(name);
  if (!it) {
    return;
  }

  let state;

  try {
    state = JSON.parse(it);
  } catch (err) {
    localStorage.removeItem(name);
    return;
  }

  if (typeof state !== "object" || state == null) {
    localStorage.removeItem(name);
    return;
  }

  return state;
}
